import { Field, Label } from '@headlessui/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoaderData, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Doctor } from '@/apiTypes.ts'
import { Breadcrumb } from '@/components/Breadcrumb.tsx'
import { StyledActionButton, StyledLinkButton } from '@/components/Buttons.tsx'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@/components/Listbox.tsx'
import { Stepper } from '@/components/Stepper.tsx'
import {
  ButtonGroup,
  FullScreenContainer,
  ScrollableContent,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { TimeSlots } from '@/components/templates/TimeSlots.tsx'
import { BodyM, Header1, Subheader } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import {
  StepEnum,
  TOTAL_STEPS,
} from '@/screens/xpert/ManualBooking/constants.ts'
import { useAppointmentStore } from '@/store.ts'
import { XpertFormStep } from '@/utils/XpertFormSteps.ts'

import { LoaderData } from './actions'
import * as S from './SelectDateTime.styled'

const StyledField = styled(Field)`
  margin-bottom: 16px;
`

const INITIAL_NUM_SLOTS = 3

const SelectDateTime = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'selectDateTime',
  })

  const navigate = useNavigate()
  const getGroupedSlots = useAppointmentStore.getState().getSortedSlots
  const getSlotsByDoctor = useAppointmentStore.getState().getSlotsByDoctor

  const getDoctorsByLocation = useAppointmentStore(
    (state) => state.getDoctorsByLocation,
  )
  const selectedSlot = useAppointmentStore((state) => state.selectedSlot)
  const { loadTime } = useLoaderData() as LoaderData

  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null)
  const [slots, setSlots] = useState(getGroupedSlots())

  const { trackFormInteraction } = useTrackFormProgress(
    XpertFormStep.SelectDateTime,
    { loadTime },
  )

  const [showAll, setShowAll] = useState(false)
  const shownSlots = showAll ? slots : slots.slice(0, INITIAL_NUM_SLOTS)
  const isAllLoaded = slots.length === shownSlots.length

  useEffect(() => {
    const locationDoctors = getDoctorsByLocation()
    setDoctors([
      { emmaId: 'all', name: t('dropdownDoctors.all') } as Doctor,
      ...locationDoctors,
    ])
  }, [])

  const handleDoctorChange = (doctorEmmaId: string) => {
    const selected =
      doctors.find((doctor) => doctor.emmaId === doctorEmmaId) || null
    setSelectedDoctor(selected)

    if (doctorEmmaId === 'all') {
      setSlots(getGroupedSlots())
    } else {
      setSlots(getSlotsByDoctor(doctorEmmaId))
    }

    trackFormInteraction({
      action: 'click',
      formFieldName: 'doctor',
      formFieldValue: selected?.name,
      formFieldValues: doctors.map((doctor) => doctor.name),
    })
  }

  return (
    <ScreenWrapper>
      <FullScreenContainer>
        <Breadcrumb
          id="back-to-previous"
          onClick={() => {
            navigate(-1)
            trackFormInteraction({
              action: 'previous',
              formFieldName: 'back-to-previous',
            })
          }}
          title={t('breadcrumbLink')}
        />
        <ScrollableContent>
          <Header1>{t('header')}</Header1>
          <Stepper total={TOTAL_STEPS} step={StepEnum.dateTime} />
          {slots?.length > 0 ? (
            <>
              <StyledField>
                <Subheader as={Label}>{t('dropdownDoctors.label')}</Subheader>
                <Listbox
                  value={selectedDoctor?.emmaId || 'all'}
                  name="doctor"
                  onChange={handleDoctorChange}
                >
                  {({ open }) => (
                    <>
                      <ListboxButton
                        onClick={() => {
                          trackFormInteraction({
                            action: open ? 'close' : 'open',
                            formFieldName: 'doctor',
                            formFieldValues: doctors.map(
                              (doctor) => doctor.name,
                            ),
                          })
                        }}
                      >
                        {selectedDoctor?.name || t('dropdownDoctors.all')}
                      </ListboxButton>
                      <ListboxOptions>
                        {doctors.map((doctor) => (
                          <ListboxOption
                            key={doctor.emmaId}
                            value={doctor.emmaId}
                          >
                            {doctor.name}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </>
                  )}
                </Listbox>
              </StyledField>

              {shownSlots && shownSlots?.length > 0 && (
                <TimeSlots
                  slots={shownSlots}
                  onSlotClick={(slot) => {
                    trackFormInteraction({
                      action: 'click',
                      formFieldName: 'timeSlot',
                      formFieldValue: `${slot.start} - ${slot.end}`,
                    })
                  }}
                />
              )}

              {!isAllLoaded && (
                <S.LoadMoreButtonWrapper>
                  <StyledLinkButton
                    variant="Primary"
                    onClick={() => {
                      setShowAll(true)
                      trackFormInteraction({
                        action: 'click',
                        formFieldName: 'showAllSlots',
                      })
                    }}
                  >
                    {t('showAllSlots')}
                  </StyledLinkButton>
                </S.LoadMoreButtonWrapper>
              )}
            </>
          ) : (
            <BodyM>{t('noSlots')}</BodyM>
          )}
        </ScrollableContent>

        <ButtonGroup>
          <StyledActionButton
            variant="Primary"
            onClick={() => {
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
              navigate('/referral/manual-booking/confirm-booking')
            }}
            disabled={selectedSlot === undefined}
          >
            {t('actions.reviewBooking')}
          </StyledActionButton>
        </ButtonGroup>
      </FullScreenContainer>
    </ScreenWrapper>
  )
}

export { SelectDateTime }
